/**
 * useTeenMode Hook
 * Provides teen theme state and colors for components to use
 */

import { useState, useEffect } from 'react';

export const useTeenMode = () => {
  // Safe initial state check
  const getInitialTeenMode = () => {
    try {
      return localStorage.getItem('teen_onboarded') === 'true';
    } catch {
      return false;
    }
  };

  const [isTeenMode, setIsTeenMode] = useState(getInitialTeenMode);
  const [theme, setTheme] = useState(null);

  useEffect(() => {
    const checkTeenMode = () => {
      try {
        const isOnboarded = localStorage.getItem('teen_onboarded') === 'true';
        setIsTeenMode(isOnboarded);

        if (isOnboarded) {
          const savedTheme = localStorage.getItem('teen_selected_theme');
          if (savedTheme) {
            try {
              setTheme(JSON.parse(savedTheme));
            } catch (e) {
              setTheme(null);
            }
          }
        } else {
          setTheme(null);
        }
      } catch (e) {
        console.warn('Error checking teen mode:', e);
        setIsTeenMode(false);
        setTheme(null);
      }
    };

    checkTeenMode();
    
    // Check periodically for changes
    const interval = setInterval(checkTeenMode, 1000);
    window.addEventListener('storage', checkTeenMode);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkTeenMode);
    };
  }, []);

  // Default colors (original app theme)
  const defaultColors = {
    base: '#FAF9F7',
    surface: '#FFFFFF',
    primary: '#2D4A2A',
    text: '#1A1D1A',
    textSecondary: '#7A7F7A',
    isDark: false,
  };

  // Return teen colors if in teen mode, otherwise defaults
  const colors = isTeenMode && theme?.colors ? {
    base: theme.colors.base || defaultColors.base,
    surface: theme.colors.surface || defaultColors.surface,
    primary: theme.colors.primary || theme.colors.accent || defaultColors.primary,
    accent: theme.colors.accent || theme.colors.accentSecondary || defaultColors.primary,
    text: theme.colors.textPrimary || defaultColors.text,
    textSecondary: theme.colors.textSecondary || defaultColors.textSecondary,
    isDark: theme.mode === 'dark',
  } : defaultColors;

  return {
    isTeenMode,
    theme,
    colors,
    isDarkMode: colors.isDark,
  };
};

export default useTeenMode;
